import React, { useState, useEffect } from 'react';
import './index.scss';
import './sidebar.scss';
import { useAdmissionProcess } from '@/Recoil/Admission/Process';
import { useSession } from '@/Recoil/Session/Session';
import { handleLinkClick } from '@/Utils/routing';
import LanguageSelector from '@/Components/LanguageSelector';
import { ErrorSnackbar } from '@/Components/Product';
import { createI18n } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';
import { StudyAbroadPaymentWall } from '@/Components/PaymentWall';
import { usePurchaseHistory } from '@/Recoil/Payment/Purchase';
import { DocumentHelpModal } from '@/Components/Admission/Modals';

interface UniversityProgressProps {
    universityId: string;
    trackId: string;
}

interface DocumentDefinition {
    name: string;
    sublabel?: string;
    example_url?: string;
    description?: string;
    instructions?: string;
    outlink?: string;
    cta?: string;
}

interface DocumentProgress {
    documentid: string;
    status: 'notSubmitted' | 'pending' | 'completed';
    file?: string;
    uploaded_at?: string;
    verified_at?: string;
}

const translations = {
    'Not Submitted': { id: 'Belum Dikirim', ko: '미인증' },
    'Pending': { id: 'Menunggu', ko: '인증 대기중' },
    'Completed': { id: 'Selesai', ko: '인증완료' },
    'Loading admission process...': { id: 'Memuat proses penerimaan...', ko: '입학 절차를 불러오는 중...' },
    'Failed to load admission process': { id: 'Gagal memuat proses penerimaan', ko: '입학 절차를 불러오지 못했습니다' },
    'Retry': { id: 'Coba Lagi', ko: '다시 시도' },
    'No admission process data': { id: 'Tidak ada data proses penerimaan', ko: '입학 절차 데이터가 없습니다' },
    'View School Introduction': { id: 'Lihat Pengenalan Sekolah', ko: '학교 소개페이지 보기' },
    'Document Preparation Achievement': { id: 'Pencapaian Persiapan Dokumen', ko: '서류 준비 전체 달성율' },
    '% Achieved': { id: '% Tercapai', ko: '%달성' },
    'items': { id: ' item', ko: '건' },
    "'s Checklist": { id: "Daftar Periksa ", ko: '님의 ' },
    'Track': { id: 'Track', ko: 'Track' },
    'You must complete all checklist items': { id: 'Anda harus menyelesaikan semua item daftar periksa', ko: '체크리스트를 모두 완료해야해요' },
    'Verified': { id: 'Diverifikasi', ko: '인증' },
    'Uploaded': { id: 'Diunggah', ko: '업로드' },
    'View File': { id: 'Lihat File', ko: '파일 보기' },
    'Re-upload': { id: 'Unggah Ulang', ko: '재업로드' },
    'Edit': { id: 'Edit', ko: '수정하기' },
    'Verify Document': { id: 'Verifikasi Dokumen', ko: '서류 인증하기' },
    'Document Verification': { id: 'Verifikasi Dokumen', ko: '서류 인증' },
    'Private': { id: 'Swasta', ko: '사립' },
    'General University': { id: 'Universitas Umum', ko: '일반대학' },
    'Sample Image': { id: 'Gambar Contoh', ko: '견본 이미지' },
    'Tip': { id: 'Tip', ko: 'Tip' },
    'Please attach a clear image so documents can be verified accurately': { id: 'Harap lampirkan gambar yang jelas agar dokumen dapat diverifikasi dengan akurat', ko: '서류를 정확하게 확인할 수 있도록 선명한 이미지를 첨부해주세요' },
    'Upload document and get verified': { id: 'Unggah dokumen dan dapatkan verifikasi', ko: '서류 업로드하고 인증받기' },
    'After uploading, Mate will accurately check your preparation status': { id: 'Setelah mengunggah, Mate akan memeriksa status persiapan Anda dengan akurat', ko: '문서를 업로드하시면 Mate가 준비 상태를 정확하게 확인해 드립니다' },
    'Upload to Mate and check if your documents are ready immediately!': { id: 'Unggah ke Mate dan periksa apakah dokumen Anda sudah siap segera!', ko: 'Mate에 업로드하고, 문서가 준비되었는지 바로 확인해 보세요!' },
    'Uploading...': { id: 'Mengunggah...', ko: '업로드 중...' },
    'Please wait': { id: 'Harap tunggu', ko: '잠시만 기다려주세요' },
    'Drag image or': { id: 'Seret gambar atau', ko: '이미지를 드래그하거나' },
    'Select from file': { id: 'Pilih dari file', ko: '파일에서 선택' },
    'JPG, PNG, PDF (max. 10MB)': { id: 'JPG, PNG, PDF (maks. 10MB)', ko: 'JPG, PNG, PDF (maks. 10MB)' },
    'Currently uploaded file': { id: 'File yang saat ini diunggah', ko: '현재 업로드된 파일' },
    'Uploaded file': { id: 'File yang diunggah', ko: '업로드된 파일' },
    'View': { id: 'Lihat', ko: '보기' },
    'Save': { id: 'Simpan', ko: '저장하기' },
    'Need help?': { id: 'Butuh bantuan?', ko: '도움이 필요하신가요?' },
    'If you have questions, please contact Mate': { id: 'Jika Anda memiliki pertanyaan, silakan hubungi Mate', ko: '궁금한 점이 있으면 Mate로 문의해 주세요' },
    'Request Help': { id: 'Minta Bantuan', ko: '도움 요청하기' },
    'Login required for input': { id: 'Login diperlukan untuk input', ko: '입력을 위해서는 로그인이 필요합니다' },
    'File uploaded and submitted successfully': { id: 'File berhasil diunggah dan dikirim', ko: '파일이 성공적으로 업로드되고 제출되었습니다' },
    'File upload failed. Please try again': { id: 'Gagal mengunggah file. Silakan coba lagi', ko: '파일 업로드에 실패했습니다. 다시 시도해 주세요' },
    'Error occurred during file upload': { id: 'Terjadi kesalahan saat mengunggah file', ko: '파일 업로드 중 오류가 발생했습니다' },
    'Help Request': { id: 'Permintaan Bantuan', ko: '도움 요청' },
    'We will help you with this document': { id: 'Kami akan membantu Anda dengan dokumen ini', ko: '이 서류에 대해 도와드리겠습니다' },
    'Send Request': { id: 'Kirim Permintaan', ko: '요청 보내기' },
    'Request sent successfully': { id: 'Permintaan berhasil dikirim', ko: '요청이 성공적으로 전송되었습니다' },
    'Failed to send request': { id: 'Gagal mengirim permintaan', ko: '요청 전송에 실패했습니다' }
};

const t = createI18n(translations);


const UniversityProgress: React.FC<UniversityProgressProps> = ({ universityId, trackId }) => {
    const {session} = useSession();
    const { hasProduct, error: purchaseError, loading: purchaseLoading } = usePurchaseHistory();
    // Treat fetch errors or loading as no purchase for safety
    const hasPurchased = !purchaseError && !purchaseLoading && hasProduct('mate_study_abroad.250917.01');
    // Force re-render when language changes
    const [, forceUpdate] = useState({});
    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);
    const { process, loading, error, syncProcess, createApplication, uploadDocument } = useAdmissionProcess();
    const [verificationSidebarOpen, setVerificationSidebarOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<{documentKey: string; progress: DocumentProgress} | null>(null);
    const [uploadingDocument, setUploadingDocument] = useState<string | null>(null);
    const [showLoginSnackbar, setShowLoginSnackbar] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [helpModalDocument, setHelpModalDocument] = useState<string>('');

    // Sync admission process data on component mount
    useEffect(() => {
        syncProcess(universityId, trackId);
    }, [syncProcess, universityId, trackId]);

    // Handle file upload using Recoil uploadDocument
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !selectedDocument) {
            return;
        }

        const file = files[0];
        const documentKey = selectedDocument.documentKey;
        
        setUploadingDocument(documentKey);
        
        try {
            const success = await uploadDocument(file, documentKey, session.userid);

            if (success) {
                // Refresh the process data to show updated status
                await syncProcess(universityId, trackId);
                alert(t('File uploaded and submitted successfully'));
            } else {
                alert(t('File upload failed. Please try again'));
            }
        } catch (error) {
            console.error('File upload error:', error);
            alert(t('Error occurred during file upload'));
        } finally {
            setUploadingDocument(null);
            // Reset the file input
            event.target.value = '';
        }
    };

    // Helper functions to transform API data
    const getDocumentsFromProcess = () => {
        if (!process) return [];
        return process.steps.flatMap(step => 
            step.sections.flatMap(section =>
                section.requirements.flatMap(req => 
                    req.documents.map(doc => ({
                        documentid: doc.documentid,
                        status: doc.submitted?.status === 'approved' ? 'completed' as const : 
                                doc.submitted?.status === 'pending' ? 'pending' as const : 'notSubmitted' as const,
                        file: doc.submitted?.file_url,
                        uploaded_at: doc.submitted?.submitted_at ? new Date(doc.submitted.submitted_at * 1000).toISOString().split('T')[0] : undefined,
                        verified_at: doc.submitted?.validated_at ? new Date(doc.submitted.validated_at * 1000).toISOString().split('T')[0] : undefined,
                    }))
                )
            )
        );
    };

    // Helper to get document status from requirement
    const getDocumentStatus = (req: any): 'completed' | 'pending' | 'notSubmitted' => {
        return req.submitted?.status === 'approved' ? 'completed' : 
               req.submitted?.status === 'pending' ? 'pending' : 'notSubmitted';
    };

    const allDocuments = getDocumentsFromProcess();
    
    const getStatusCount = (status: string) => {
        return allDocuments.filter(doc => doc.status === status).length;
    };

    // Status label mapping for UI display
    const getStatusLabel = (status: string) => {
        const labels = {
            notSubmitted: t('Not Submitted'),
            pending: t('Pending'),
            completed: t('Completed')
        };
        return labels[status as keyof typeof labels] || status;
    };

    const getCompletionPercentage = () => {
        const completed = getStatusCount('completed');
        const pending = getStatusCount('pending');
        return Math.round(((completed + pending * 0.5) / allDocuments.length) * 100);
    };

    const getDocumentDefinition = (documentKey: string): DocumentDefinition => {
        if (process) {
            const doc = process.documents.find(d => d.documentid === documentKey);
            if (doc) {
                return {
                    name: doc.name,
                    description: doc.description,
                    example_url: doc.example_url,
                    instructions: doc.instructions,
                    outlink: doc.outlink,
                    cta: doc.cta,
                };
            }
        }
        return { name: documentKey };
    };

    const openVerificationSidebar = (documentKey: string, documentProgress: DocumentProgress) => {
        if (!session.email) {
            setShowLoginSnackbar(true);
            setTimeout(() => setShowLoginSnackbar(false), 3000);
        }
        if (!process?.application) {
            const applicationId = `app-${Date.now()}-${session.userid}`;
            createApplication(universityId, trackId, applicationId);
        }

        setSelectedDocument({documentKey, progress: documentProgress});
        setVerificationSidebarOpen(true);
        // Push a new history entry to handle back button
        window.history.pushState({modal: 'verification'}, '');
    };

    const closeVerificationSidebar = () => {
        setVerificationSidebarOpen(false);
        setSelectedDocument(null);
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && verificationSidebarOpen) {
                closeVerificationSidebar();
            }
        };

        const handlePopState = () => {
            if (verificationSidebarOpen) {
                closeVerificationSidebar();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('popstate', handlePopState);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [verificationSidebarOpen]);
    
    if (!process) {
        return (
            <div className={`Progress ${!hasPurchased ? 'payment-restricted' : ''}`}>
                <div className="Container">
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>{t('No admission process data')}</p>
                        <button onClick={() => syncProcess(universityId, trackId)}>{t('Retry')}</button>
                    </div>
                </div>
            </div>
        );
    }

    const currentUniversity = process.university;
    const currentSteps = process.steps;

    return (
        <div className={`Progress ${!hasPurchased ? 'payment-restricted' : ''}`}>
            <StudyAbroadPaymentWall productid="mate_study_abroad.250917.01" forceShow />
            <div className="Container">
                {/* University Card */}
                <div className="UniversityCard">
                    <div className="Left">
                        <div 
                            className="Logo" 
                            style={{backgroundImage: `url('${currentUniversity.logo_url || '/img/default-university-logo.png'}')`}}
                        ></div>
                        <div className="Info">
                            <div className="Header">
                                <h2 className="Title">{currentUniversity.name || currentUniversity.english_name}</h2>
                                <div className="Badges">
                                    {currentUniversity.badges?.map((badge, index) => (
                                        <div key={index} className={`Badge ${badge.type}`}>
                                            {badge.text}
                                        </div>
                                    )) || (
                                        <>
                                            <div className="Badge primary">{t('Private')}</div>
                                            <div className="Badge secondary">{t('General University')}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <p className="Address">{currentUniversity.address || ''}</p>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                        <LanguageSelector onChange={async (newLang) => {
                            languageManager.setLanguage(newLang as any);
                            // Re-fetch admission process data with new language header
                            await syncProcess(universityId, trackId);
                        }} />
                        <a
                            className="Action Button"
                            href={`/admission/${universityId}`}
                            onClick={handleLinkClick}
                        >
                            {t('View School Introduction')}
                        </a>
                    </div>
                </div>
                
                {/* Progress Overview */}
                <div className="ProgressOverview">
                    <h3 className="Title">{t('Document Preparation Achievement')}</h3>
                    <div className="Content">
                        <div className="Header">
                            <div className="Percentage">{getCompletionPercentage()}{t('% Achieved')}</div>
                            <div className="Stats">
                                {['notSubmitted', 'pending', 'completed'].map((status) => (
                                    <div key={status} className="Stat">
                                        <div className={`StatBadge ${status}`}>
                                            <div className="Dot"></div>
                                            <span>{getStatusLabel(status)}</span>
                                        </div>
                                        <div className="StatCount">{getStatusCount(status)}{t('items')}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="ProgressBar">
                            <div className="Track">
                                <div 
                                    className="Fill completed"
                                    style={{width: `${(getStatusCount('completed') / allDocuments.length) * 100}%`}}
                                ></div>
                                <div 
                                    className="Fill pending"
                                    style={{
                                        width: `${(getStatusCount('pending') / allDocuments.length) * 100}%`,
                                        left: `${(getStatusCount('completed') / allDocuments.length) * 100}%`
                                    }}
                                ></div>
                            </div>
                            <div className="PercentageLabel">{getCompletionPercentage()}%</div>
                        </div>
                    </div>
                </div>
                
                {/* Checklist Header */}
                <div className="ChecklistHeader">
                    <h2 className="Title">{`${session.name}${t("'s Checklist")}${currentUniversity.name} (${process.track.name} ${t('Track')})`}</h2>
                    <p className="Description">{t('You must complete all checklist items')}</p>
                </div>
                
                {/* Checklist Items */}
                <div className="ChecklistItems">
                    {currentSteps.map((step) => (
                        <div key={step.stepid} className="Section">
                            <h2 className="SectionMainTitle">{step.name}</h2>

                            {step.sections.map(section => (
                            
                                <div key={section.section} className="SectionGroup">
                                    {section.section && <h3 className="SectionTitle">{section.section}</h3>}
                                    {section.requirements.map((requirement) => (
                                        <div key={requirement.requirementid} className="ItemsGroup">
                                            <div className="GroupHeader">
                                                <span className="Category">{requirement.name}</span>
                                                {requirement.description && <span className="Status">・ {requirement.description}</span>}
                                            </div>
                                            {requirement.documents.map((doc, index) => {
                                                const docDef = getDocumentDefinition(doc.documentid);
                                                const status = getDocumentStatus(requirement);
                                                const showBadge = requirement.type === "alternative" ? index === 0 : true;
                                                const documentProgress = {
                                                    documentid: doc.documentid,
                                                    status,
                                                    file: doc.submitted?.file_url,
                                                    uploaded_at: doc.submitted?.submitted_at ? new Date(doc.submitted.submitted_at * 1000).toISOString().split('T')[0] : undefined,
                                                    verified_at: doc.submitted?.validated_at ? new Date(doc.submitted.validated_at * 1000).toISOString().split('T')[0] : undefined,
                                                };
                                                return (
                                                    <div key={doc.documentid} className="Item">
                                                        {showBadge && (
                                                            <div className={`StatusBadge ${status}`}>
                                                                <div className="Dot"></div>
                                                                <span>{getStatusLabel(status)}</span>
                                                            </div>
                                                        )}
                                                        {!showBadge && (
                                                            <div className="StatusBadge spacer" style={{opacity: 0}}>
                                                                <div className="Dot"></div>
                                                                <span>{t('Not Submitted')}</span>
                                                            </div>
                                                        )}
                                                        <div className="Info">
                                                            <div className="Title">
                                                                {docDef.name}
                                                                {documentProgress.verified_at ? (
                                                                    <span className="Subtitle"> ({t('Verified')}: {documentProgress.verified_at})</span>
                                                                ):
                                                                documentProgress.uploaded_at ? !documentProgress.verified_at && (
                                                                    <span className="Subtitle"> ({t('Uploaded')}: {documentProgress.uploaded_at})</span>
                                                                ):
                                                                docDef.sublabel ? (
                                                                    <span className="Subtitle"> ({docDef.sublabel})</span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="Actions">
                                                            {documentProgress.file && (
                                                                <button 
                                                                    className="Action Secondary"
                                                                    onClick={() => window.open(documentProgress.file, '_blank')}
                                                                >
                                                                    {t('View File')}
                                                                </button>
                                                            )}
                                                            <button 
                                                                className="Action Primary"
                                                                onClick={() => openVerificationSidebar(doc.documentid, documentProgress)}
                                                            >
                                                                {status === 'completed' ? t('Re-upload') :
                                                                status === 'pending' ? t('Edit') : t('Verify Document')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>

                            ))}
                            
                            {/* Section End Note */}
                            {step.description && (
                                <div className="SectionEndNote">
                                    <div className="Content">
                                        <p className="Text">{step.description}</p>
                                        {step.end_cta && (
                                            <button 
                                                className="Button"
                                                style={!step.outlink ? {opacity: 0.5, filter: `grayscale(0.5)`} : {}}
                                                type="button"
                                                disabled={!step.outlink}
                                                onClick={() => step.outlink && window.open(step.outlink, '_blank')}
                                            >
                                                {step.end_cta}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Document Verification Sidebar - Redesigned to match original */}
            {verificationSidebarOpen && selectedDocument && (
                <div className="DocumentDrawer">
                    <div className="DocumentDrawer-overlay" onClick={closeVerificationSidebar}></div>
                    <div className="DocumentDrawer-content">
                        {/* Header */}
                        <div className="DocumentDrawer-header">
                            <div className="DocumentDrawer-universityInfo">
                                <div className="DocumentDrawer-logo">
                                    <img src={currentUniversity.logo_url || '/img/default-university-logo.png'} alt="University Logo" />
                                </div>
                                <div className="DocumentDrawer-universityDetails">
                                    <h2 className="DocumentDrawer-universityName">{currentUniversity.name} {t('Document Verification')}</h2>
                                    <div className="DocumentDrawer-badges">
                                        {currentUniversity.badges?.map((badge, index) => (
                                            <div key={index} className={`DocumentDrawer-badge DocumentDrawer-badge--${badge.type === 'primary' ? 'private' : 'general'}`}>
                                                {badge.text}
                                            </div>
                                        )) || (
                                            <>
                                                <div className="DocumentDrawer-badge DocumentDrawer-badge--private">{t('Private')}</div>
                                                <div className="DocumentDrawer-badge DocumentDrawer-badge--general">{t('General University')}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button className="DocumentDrawer-closeButton" onClick={closeVerificationSidebar}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>

                        {/* Document Section */}
                        <div className="DocumentDrawer-body">
                            <div className="DocumentDrawer-documentSection">
                                <div className="DocumentDrawer-documentHeader">
                                    <div className="DocumentDrawer-statusBadge">
                                        <span className="DocumentDrawer-statusText">{getStatusLabel(selectedDocument.progress.status)}</span>
                                        <div className={`DocumentDrawer-statusDot ${selectedDocument.progress.status}`}></div>
                                    </div>
                                    <h3 className="DocumentDrawer-documentTitle">
                                        {getDocumentDefinition(selectedDocument.documentKey).name}
                                    </h3>
                                </div>

                                {/* Document Guide Section */}
                                <div className="DocumentDrawer-documentGuide">
                                    {/* Upload Description */}
                                    <div className="DocumentDrawer-uploadDescription">
                                        <p>{getDocumentDefinition(selectedDocument.documentKey).description}</p>
                                    </div>

                                    {/* Sample Image Section */}
                                    {getDocumentDefinition(selectedDocument.documentKey).example_url && (
                                        <div className="DocumentDrawer-sampleImageSection">
                                            <div className="DocumentDrawer-sampleImageContainer">
                                                <img 
                                                    src={getDocumentDefinition(selectedDocument.documentKey).example_url} 
                                                    alt={t('Sample Image')} 
                                                    className="DocumentDrawer-sampleImage" 
                                                />
                                            </div>
                                            {/* Tip Box */}
                                            <div className="DocumentDrawer-tipBox DocumentDrawer-tipBox--attached">
                                                <span className="DocumentDrawer-tipLabel">{t('Tip')}</span>
                                                <p className="DocumentDrawer-tipText">{t('Please attach a clear image so documents can be verified accurately')}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Link Button */}
                                    {getDocumentDefinition(selectedDocument.documentKey).outlink && (
                                        <div className="DocumentDrawer-linkButton">
                                            <button 
                                                className="DocumentDrawer-linkBtn"
                                                onClick={() => window.open(getDocumentDefinition(selectedDocument.documentKey).outlink, '_blank')}
                                            >
                                                {getDocumentDefinition(selectedDocument.documentKey).cta}
                                            </button>
                                        </div>
                                    )}

                                    {/* Upload Section */}
                                    <div className="DocumentDrawer-uploadSection">
                                        {/* Upload Title Section */}
                                        <h3 className="DocumentDrawer-uploadTitle">{t('Upload document and get verified')}</h3>
                                        <p className="DocumentDrawer-uploadDesc">
                                            {t('After uploading, Mate will accurately check your preparation status')}
                                            {t('Upload to Mate and check if your documents are ready immediately!')}
                                        </p>
                                        
                                        {/* Figma Upload Component */}
                                        <div className="DocumentDrawer-figmaUpload">
                                            <div className="DocumentDrawer-figmaUploadArea">
                                                <div className="DocumentDrawer-figmaUploadContent">
                                                    <div className="DocumentDrawer-figmaUploadIcon">
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M7 10L12 15L17 10" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M12 15V3" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                    <div className="DocumentDrawer-figmaUploadText">
                                                        {uploadingDocument === selectedDocument?.documentKey ? (
                                                            <>
                                                                <p className="DocumentDrawer-figmaUploadTitle">{t('Uploading...')}</p>
                                                                <p className="DocumentDrawer-figmaUploadSubtitle">{t('Please wait')}</p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="DocumentDrawer-figmaUploadTitle">{t('Drag image or')}</p>
                                                                <p className="DocumentDrawer-figmaUploadSubtitle">{t('Select from file')}</p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <input 
                                                    type="file" 
                                                    className="DocumentDrawer-uploadInput" 
                                                    accept=".jpg,.jpeg,.png,.pdf" 
                                                    onChange={handleFileUpload}
                                                    disabled={uploadingDocument === selectedDocument?.documentKey}
                                                />
                                            </div>
                                            <div className="DocumentDrawer-figmaUploadInfo">
                                                <span className="DocumentDrawer-figmaUploadFormat">{t('JPG, PNG, PDF (max. 10MB)')}</span>
                                            </div>
                                        </div>

                                        {/* Current File Display */}
                                        {selectedDocument.progress.file && (
                                            <div className="DocumentDrawer-currentFile">
                                                <h5 className="DocumentDrawer-currentFileTitle">{t('Currently uploaded file')}</h5>
                                                <div className="DocumentDrawer-currentFileItem">
                                                    <span className="DocumentDrawer-currentFileName">
                                                        {selectedDocument.progress.file.split('/').pop() || t('Uploaded file')}
                                                    </span>
                                                    <button 
                                                        className="DocumentDrawer-currentFileView"
                                                        onClick={() => window.open(selectedDocument.progress.file, '_blank')}
                                                    >
                                                        {t('View')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Save Button */}
                                        <button className="DocumentDrawer-saveButton">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {t('Save')}
                                        </button>
                                    </div>

                                    {/* Service Box */}
                                    <div className="DocumentDrawer-serviceBox">
                                        <div className="DocumentDrawer-serviceHeader">
                                            <h4 className="DocumentDrawer-serviceTitle">{t('Need help?')}</h4>
                                            <p className="DocumentDrawer-serviceDescription">{t('If you have questions, please contact Mate')}</p>
                                        </div>
                                        <button className="DocumentDrawer-serviceButton" onClick={() => {
                                            const docDef = getDocumentDefinition(selectedDocument.documentKey);
                                            setHelpModalDocument(docDef.name);
                                            setShowHelpModal(true);
                                        }}>{t('Request Help')}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {loading
            ?<div className="LoadingOverlay" style={{
                position: 'fixed',
                width: '100%', height: '100%',
                top: 0, left: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000
            }}>
                <div className="Progress">
                    <div className="Container">
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <p>{t('Loading admission process...')}</p>
                        </div>
                    </div>
                </div>
            </div>
            :error
            ?<div className="Progress">
                <div className="Container">
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>{t('Failed to load admission process')}: {error}</p>
                        <button onClick={() => syncProcess(universityId, trackId)}>{t('Retry')}</button>
                    </div>
                </div>
            </div>
            :null}

            {/* Login Required Snackbar */}
            <ErrorSnackbar
                message={t('Login required for input')}
                show={showLoginSnackbar}
            />

            {/* Document Help Modal */}
            <DocumentHelpModal
                isOpen={showHelpModal}
                onClose={() => setShowHelpModal(false)}
                documentName={helpModalDocument}
                universityName={currentUniversity?.name || ''}
                trackName={process?.track?.name || ''}
                sessionData={{
                    name: session?.name,
                    email: session?.email
                }}
            />
        </div>
    );
};

export default UniversityProgress;
